import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Empty, TicketStatus } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Lightbulb, BookOpen, GraduationCap, Search, Plus, PlayCircle, Send, MessageSquare, Loader2, Paperclip } from "lucide-react";

export async function uploadTicketFiles(files, presignPath, presignParams = {}) {
  const out = [];
  for (const file of Array.from(files || [])) {
    const ct = file.type || "application/octet-stream";
    const { data } = await api.post(presignPath, { filename: file.name, content_type: ct, size: file.size }, { params: presignParams });
    await fetch(data.upload_url, { method: "PUT", body: file, headers: { "Content-Type": ct } });
    out.push({ key: data.key, filename: data.filename, content_type: ct, size: file.size });
  }
  return out;
}

export function AttachmentChips({ ticketId, atts, base }) {
  if (!atts?.length) return null;
  const open = async (key) => {
    try {
      const { data } = await api.get(`${base}/${ticketId}/attachment`, { params: { key } });
      const a = document.createElement("a");
      a.href = data.url; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {atts.map((a) => (
        <button key={a.key} data-testid={`ticket-att-${a.key}`} onClick={() => open(a.key)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-xs hover:border-indigo-300 transition-colors">
          <Paperclip className="h-3 w-3" /> {a.filename}
        </button>
      ))}
    </div>
  );
}

export default function SupportHelp() {
  return (
    <div>
      <div className="mb-6">
        <div className="overline text-azx-collection">Support & Help</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Support & Help Center</h1>
        <p className="mt-1 text-sm text-slate-500">Tickets, suggestions, knowledge base and onboarding academy.</p>
      </div>
      <Tabs defaultValue="tickets">
        <TabsList data-testid="support-tabs">
          <TabsTrigger value="tickets" data-testid="tab-tickets"><LifeBuoy className="mr-1.5 h-4 w-4" /> Tickets</TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb"><BookOpen className="mr-1.5 h-4 w-4" /> Knowledge Base</TabsTrigger>
          <TabsTrigger value="academy" data-testid="tab-academy"><GraduationCap className="mr-1.5 h-4 w-4" /> Academy</TabsTrigger>
          <TabsTrigger value="suggest" data-testid="tab-suggest"><Lightbulb className="mr-1.5 h-4 w-4" /> Suggestions</TabsTrigger>
        </TabsList>
        <TabsContent value="tickets" className="mt-5"><Tickets /></TabsContent>
        <TabsContent value="kb" className="mt-5"><KnowledgeBase /></TabsContent>
        <TabsContent value="academy" className="mt-5"><Academy /></TabsContent>
        <TabsContent value="suggest" className="mt-5"><Suggestions /></TabsContent>
      </Tabs>
    </div>
  );
}

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [f, setF] = useState({ subject: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.subject || !f.body) return toast.error("Subject and details required");
    setBusy(true);
    try {
      const attachments = await uploadTicketFiles(files, "/support/upload-url");
      await api.post("/support/tickets", { ...f, attachments });
      toast.success("Ticket submitted — our support team has been notified");
      setF({ subject: "", body: "" }); setFiles([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div>
        {tickets.length === 0 ? <Empty icon={LifeBuoy} title="No tickets yet" hint="Raise a ticket and our support team will get back to you." /> : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((t) => (
              <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setOpen(t.id)}
                className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{t.subject}</span>
                  <TicketStatus status={t.status} />
                </div>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{t.body}</p>
                <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
                  <span>{t.raised_by_name}</span><span>·</span>
                  <span>{new Date(t.updated_at || t.created_at).toLocaleString()}</span>
                  {t.attachments?.length ? <span className="inline-flex items-center gap-1 text-slate-400"><Paperclip className="h-3 w-3" />{t.attachments.length}</span> : null}
                  {t.messages?.length ? <span className="inline-flex items-center gap-1 text-indigo-500"><MessageSquare className="h-3 w-3" />{t.messages.length}</span> : null}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 h-fit">
        <div className="font-heading font-semibold mb-4">Submit a ticket</div>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input data-testid="ticket-subject" className="mt-1.5" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
          <div><Label>Details</Label><Textarea data-testid="ticket-body" className="mt-1.5" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></div>
          <div>
            <Label className="text-xs text-slate-500">Attach a screenshot or file (optional)</Label>
            <Input type="file" multiple data-testid="ticket-file-input" className="mt-1.5 text-xs" onChange={(e) => setFiles(e.target.files)} />
            {files.length > 0 && <div className="mt-1 text-[11px] text-slate-400">{files.length} file(s) selected</div>}
          </div>
          <Button onClick={submit} disabled={busy} data-testid="ticket-submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Submit
          </Button>
        </div>
      </div>
      {open && <TicketThread ticketId={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function TicketThread({ ticketId, onClose }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/support/tickets/${ticketId}`).then((r) => setT(r.data)).catch(() => {}), [ticketId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!reply.trim() && files.length === 0) return;
    setBusy(true);
    try {
      const attachments = await uploadTicketFiles(files, "/support/upload-url");
      await api.post(`/support/tickets/${ticketId}/reply`, { body: reply, attachments });
      setReply(""); setFiles([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ticket-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">{t?.subject || "Ticket"}{t && <TicketStatus status={t.status} />}</DialogTitle>
        </DialogHeader>
        {!t ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3 text-sm">
              <div className="text-[11px] text-slate-400 mb-1">{t.raised_by_name} · {new Date(t.created_at).toLocaleString()}</div>
              <p className="whitespace-pre-wrap">{t.body}</p>
              <AttachmentChips ticketId={ticketId} atts={t.attachments} base="/support/tickets" />
            </div>
            <div className="mt-3 space-y-3 max-h-[40vh] overflow-y-auto" data-testid="ticket-messages">
              {t.messages.map((m) => (
                <div key={m.id} className={`rounded-lg p-3 text-sm ${m.author_side === "founder" ? "bg-indigo-50 dark:bg-indigo-950/40" : "bg-slate-50 dark:bg-slate-900"}`}>
                  <div className="text-[11px] text-slate-400 mb-1">{m.author_side === "founder" ? "AzexPan Support" : m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <AttachmentChips ticketId={ticketId} atts={m.attachments} base="/support/tickets" />
                </div>
              ))}
              {t.messages.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No replies yet.</p>}
            </div>
            {t.status !== "closed" ? (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Textarea data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="min-h-[70px]" />
                  <Button data-testid="ticket-reply-send" onClick={send} disabled={busy} className="self-end bg-indigo-600 hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                  <Paperclip className="h-3.5 w-3.5" />
                  <input type="file" multiple data-testid="ticket-reply-file" className="text-xs" onChange={(e) => setFiles(e.target.files)} />
                </label>
              </div>
            ) : <p className="mt-3 text-xs text-slate-400 text-center">This ticket is closed.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KnowledgeBase() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/kb${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data)); }, [q]);
  return (
    <div>
      <div className="relative max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="pl-9" />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((a) => (
          <div key={a.id} data-testid={`kb-${a.id}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="overline text-indigo-500">{a.category}</div>
            <div className="mt-1 font-heading font-semibold">{a.title}</div>
            <p className="mt-1.5 text-sm text-slate-500">{a.body}</p>
          </div>
        ))}
        {items.length === 0 && <Empty title="No articles found" />}
      </div>
    </div>
  );
}

function Academy() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/academy").then((r) => setItems(r.data)); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {items.map((a) => (
        <div key={a.id} data-testid={`academy-${a.id}`} className="flex gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/40"><PlayCircle className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <div className="font-heading font-semibold">{a.title}</div>
            <p className="text-sm text-slate-500">{a.summary}</p>
            <div className="mt-1 text-[11px] font-mono text-slate-400">{a.duration}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Suggestions() {
  const [body, setBody] = useState("");
  const submit = async () => {
    if (!body.trim()) return;
    try { await api.post("/support/suggestions", { body }); toast.success("Thanks for the suggestion!"); setBody(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-lg rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-5 w-5 text-amber-500" /><span className="font-heading font-semibold">Suggestion box</span></div>
      <p className="text-sm text-slate-500 mb-3">Have an idea to improve AzexPan? We'd love to hear it.</p>
      <Textarea data-testid="suggestion-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Your idea…" />
      <Button onClick={submit} data-testid="suggestion-submit" className="mt-3 bg-indigo-600 hover:bg-indigo-700">Send suggestion</Button>
    </div>
  );
}
